import React, { useMemo } from 'react'
import {
	FormControl,
	InputLabel,
	Select as MuiSelect,
	type SelectProps as MuiSelectProps,
	FormHelperText,
	MenuItem,
	InputLabelProps,
	FormControlProps as MuiFormControlProps,
	type SelectChangeEvent as MuiSelectChangeEvent,
} from '@mui/material'
import {
	Controller,
	type FieldErrors,
	useFormContext,
} from 'react-hook-form'
import useFormField from '../../hooks/useFormField'
import { RFHControl } from '../../types'

type ItemProps<T> = {
	disabled?: boolean
} & T

export type SelectChangeEvent<T> = MuiSelectChangeEvent<T>

export type SelectProps<T> = {
	name?: string
	menuItems: ItemProps<T>[]
	children?: React.ReactNode
	labelField?: string
	valueField?: string
	control?: RFHControl
	errors?: FieldErrors
	helperText?: string
	selectProps?: Omit<MuiSelectProps, 'disabled' | 'displayEmpty'>
	labelProps?: InputLabelProps
	renderItem?: (item: ItemProps<T>) => React.ReactNode
	displayEmpty?: boolean
	label?: string
	placeholder?: string
	disabled?: boolean
	/**
	 * Whether the Select should be controlled by `react-hook-form`.
	 * When `true`, uses Controller wrapper. When `false`, renders as uncontrolled component.
	 * Defaults to `true`.
	 * */
	controlled?: boolean
} & MuiFormControlProps

const Select = <T,>({
	variant = 'standard',
	name,
	selectProps,
	labelProps,
	label,
	placeholder,
	children,
	errors: propsErrors,
	control: propsControl,
	defaultValue,
	displayEmpty,
	menuItems,
	labelField = 'label',
	valueField = 'value',
	renderItem,
	helperText,
	disabled,
	controlled = true,
	...rest
}: SelectProps<T>) => {
	const formContext = useFormContext()
	const control = propsControl || formContext?.control
	const errors = propsErrors || formContext?.formState.errors

	const { getError } = useFormField()
	const error = getError(name, errors)
	const labelId = `${name || label}-label`

	const EmptyMenuItem = useMemo(
		() =>
			displayEmpty ? (
				<MenuItem value="">
					<em>{placeholder || 'Select value'}</em>
				</MenuItem>
			) : undefined,
		[displayEmpty, placeholder]
	)

	const MenuItems: React.ReactNode[] | undefined = useMemo(
		() =>
			menuItems
				? menuItems.map((item: ItemProps<T>, i) => {
						if (renderItem) {
							return renderItem(item)
						}

						return (
							<MenuItem
								key={`${name}-menu-item-${i}`}
								value={
									item[valueField as keyof ItemProps<T>] as string
								}
								disabled={item.disabled}
							>
								{item[labelField as keyof ItemProps<T>] as string}
							</MenuItem>
						)
					})
				: undefined,
		[labelField, menuItems, name, renderItem, valueField]
	)

	return (
		<FormControl {...rest} variant={variant} error={!!error}>
			{label && (
				<InputLabel {...labelProps} id={labelId}>
					{label}
				</InputLabel>
			)}
			{control && name && controlled ? (
				<Controller
					name={name}
					control={control}
					defaultValue={defaultValue}
					render={({ field }) => {
						const handleSelectChange = (
							event: SelectChangeEvent<unknown>,
							child: React.ReactNode
						) => {
							field.onChange(event)
							selectProps?.onChange?.(event, child)
						}

						return (
							<MuiSelect
								{...selectProps}
								{...field}
								onChange={handleSelectChange}
								disabled={disabled}
								labelId={labelId}
							>
								{EmptyMenuItem}
								{MenuItems}
								{children}
							</MuiSelect>
						)
					}}
				/>
			) : (
				<MuiSelect
					name={name}
					defaultValue={defaultValue}
					{...selectProps}
					disabled={disabled}
					labelId={labelId}
				>
					{EmptyMenuItem}
					{MenuItems}
					{children}
				</MuiSelect>
			)}

			{!error && <FormHelperText>{helperText ?? ' '}</FormHelperText>}
			{error && <FormHelperText>{error.message}</FormHelperText>}
		</FormControl>
	)
}

export default Select
